import { Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import logoMark from '@/assets/nolatr-logo.png';
import { useEdition } from '@/editions';
import { cn } from '@/lib/cn';

/**
 * The logo, linking home (§ Hoofdmenu: "Links de wordmark 'NOLATR'"). The
 * artwork is a solid black PNG with a transparent background, used as a
 * mask over `bg-ink` rather than drawn directly — `--ink` already flips
 * black/white per theme (`src/index.css`), so the mark repaints with the
 * rest of the chrome instead of needing a second, pre-inverted asset.
 *
 * The redesign adds a small mint mark before the wordmark and, when the
 * caller's edition names one (`EditionDefinition.brandSuffix`, e.g. Fit's
 * `'FIT'`), a suffix after it — so the sidebar reads "NOLATR FIT" instead
 * of the bare wordmark.
 */
export function Brand({ className }: { className?: string }) {
  const { t } = useTranslation();
  const edition = useEdition();
  const brandName = edition.brandSuffix
    ? `${t('shell.brandName')} ${edition.brandSuffix}`
    : t('shell.brandName');

  return (
    <Link to="/" className={cn('inline-flex shrink-0 items-center gap-1.5', className)}>
      <span className="sr-only">{brandName}</span>
      <Activity className="text-mint size-5 shrink-0" aria-hidden="true" />
      <span
        aria-hidden="true"
        className="bg-ink block h-5 w-[98px] shrink-0"
        style={{
          maskImage: `url(${logoMark})`,
          maskRepeat: 'no-repeat',
          maskSize: 'contain',
          maskPosition: 'left center',
          WebkitMaskImage: `url(${logoMark})`,
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskSize: 'contain',
          WebkitMaskPosition: 'left center',
        }}
      />
      {edition.brandSuffix && (
        <span aria-hidden="true" className="text-mint text-sm font-extrabold tracking-wide">
          {edition.brandSuffix}
        </span>
      )}
    </Link>
  );
}
